import { PhoneCallView, ZoomPhoneCallState } from '@ndvr/customer-api';

export interface CallStateMeta {
    label: string;
    badgeClassName: string;
}

export const CALL_STATE_META: Record<ZoomPhoneCallState, CallStateMeta> = {
    [ZoomPhoneCallState.RECORDED]: { label: 'Recorded', badgeClassName: 'bg-[#E8FFFF] text-[#07606D]' },
    [ZoomPhoneCallState.NOT_RECORDED]: { label: 'Not recorded', badgeClassName: 'bg-[#F8F7F5] text-[#716664]' },
    [ZoomPhoneCallState.VOICEMAIL]: { label: 'Voicemail', badgeClassName: 'bg-[#FFF6EB] text-[#7D552B]' },
    [ZoomPhoneCallState.MISSED]: { label: 'Missed', badgeClassName: 'bg-[#FCECEB] text-[#B03731]' },
};

export const isOutbound = (call: PhoneCallView): boolean => call.direction?.toLowerCase() === 'outbound';

/** The household-side party of the call: the callee for outbound calls, the caller otherwise. */
export const callCounterpartyName = (call: PhoneCallView): string =>
    (isOutbound(call) ? call.toName || call.toNumber : call.fromName || call.fromNumber) || 'Unknown caller';

/** Seconds to `m:ss`; em dash when the call never connected. */
export const formatCallDuration = (seconds?: number): string => {
    if (!seconds || seconds <= 0) {
        return '—';
    }
    const minutes = Math.floor(seconds / 60);
    const remainder = Math.floor(seconds % 60);
    return `${minutes}:${String(remainder).padStart(2, '0')}`;
};

/** Milliseconds offset within the recording to `m:ss`. */
export const formatTranscriptOffset = (offsetMs?: number): string => {
    const totalSeconds = Math.max(0, Math.floor((offsetMs ?? 0) / 1000));
    return `${Math.floor(totalSeconds / 60)}:${String(totalSeconds % 60).padStart(2, '0')}`;
};

const SPEAKER_COLORS = ['#2C6E72', '#8A6233', '#6E5BA6', '#2F7D52', '#B5562B', '#3A6FA0'];

/** Stable per-speaker accent color for transcript avatars and names. */
export const speakerColor = (speakerName: string): string => {
    let hash = 0;
    for (let i = 0; i < speakerName.length; i += 1) {
        hash = (hash * 31 + speakerName.charCodeAt(i)) | 0;
    }
    return SPEAKER_COLORS[Math.abs(hash) % SPEAKER_COLORS.length];
};

export const speakerInitials = (speakerName: string): string => {
    const parts = speakerName.trim().split(/\s+/);
    return (((parts[0]?.[0] ?? '') + (parts[1]?.[0] ?? '')).toUpperCase() || '?').slice(0, 2);
};
